import { ChangeDetectionStrategy, Component, DestroyRef, effect, inject, OnInit, TemplateRef, untracked, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, ReactiveFormsModule, Validators } from '@angular/forms';

import {
  AppError,
  ButtonModule,
  DIALOG_DATA,
  DialogRef,
  IconModule,
  isEmpty,
  OkDialog,
  RadioOptionModel,
  RadioOptionModule,
  SelectModule,
  SimpleInputModule,
  TextAreaComponentModule,
} from '@hglobal/fin-ui';
import { FormBuilder, FormControl, FormGroup } from '@ngneat/reactive-forms';
import { GroupResolverFormBuilder } from '@ngneat/reactive-forms/lib/form-builder';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { WalletCardComponent } from '@kr/components';
import { environment } from '@kr/environment';
import { formatBalanceByCurrency, Transfer, WalletCard } from '@kr/models';
import { validateTransferAmount, validateTransferMinAmount } from '@kr/validators';

import { TRANSFER_API } from './api/transfer-api.interface';
import { TransferApiService } from './api/transfer-api.service';
import { TransferDetailsDialog } from './components';
import { TransferApiMockService } from './mocks/transfer-api-mock.service';
import { TransferStore } from './transfer.store';
import { digitPattern } from '../../core/helpers/digit-pattern';

export type TransferTabs = 'internal' | 'external';
const PAYOUT_ESTIMATE_ERROR = '11018';

@Component({
  imports: [
    TranslateModule,
    IconModule,
    ButtonModule,
    WalletCardComponent,
    ReactiveFormsModule,
    SimpleInputModule,
    TextAreaComponentModule,
    SelectModule,
    RadioOptionModule,
  ],
  providers: [
    TransferStore,
    {
      provide: TRANSFER_API,
      useClass: environment.useMocks ? TransferApiMockService : TransferApiService,
    },
    TransferDetailsDialog,
  ],
  selector: 'kr-transfer',
  templateUrl: './transfer.component.html',
  styleUrls: ['./transfer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TransferComponent implements OnInit {
  readonly #fb = inject(FormBuilder);
  readonly #dialogRef = inject(DialogRef);
  readonly #destroyRef = inject(DestroyRef);
  readonly #transferDetailsDialog = inject(TransferDetailsDialog);
  readonly #okDialog = inject(OkDialog);
  readonly #translate = inject(TranslateService);
  readonly #store = inject(TransferStore);
  readonly data = inject<{ wallet: WalletCard; wallets: WalletCard[] }>(DIALOG_DATA);

  failedTpl = viewChild<TemplateRef<unknown>>('failedTpl');

  wallet: WalletCard;
  wallets: WalletCard[];
  form: FormGroup<GroupResolverFormBuilder<Transfer>>;
  tab: TransferTabs = 'external';
  errMessage: string;

  coinIdOptions: { primary: RadioOptionModel; secondary: RadioOptionModel };

  readonly estimatesSucceed = effect(() => {
    const isInternalSuccess = this.#store.isGetEstimatesTransferInternalSuccess();
    const isExternalSuccess = this.#store.isGetEstimatesTransferExternalSuccess();
    if (isInternalSuccess || isExternalSuccess) {
      untracked(() => {
        this.#openExecuteTransfer();
      });
    }
  });

  readonly estimatesFailed = effect(() => {
    const internalError = this.#store.getEstimatesTransferInternalError();
    const externalError = this.#store.getEstimatesTransferExternalError();

    if (internalError || externalError) {
      untracked(() => {
        this.openFailedDialog(internalError || externalError);
      });
    }
  });

  get walletTransferMinAmount(): number {
    const amount = this.#isPrimaryCoinSelected ? this.wallet.coin.minAmount : this.wallet.secondaryWalletCoin.minAmount;

    return Number(amount);
  }

  get walletTransferMaxDecimals(): number {
    const amount = this.#isPrimaryCoinSelected ? this.wallet.coin.decimals : this.wallet.secondaryWalletCoin.decimals;

    return Number(amount);
  }

  get #isPrimaryCoinSelected(): boolean {
    if (!this.form || !this.coinIdOptions) {
      return true;
    }

    return this.#coinIdCtrl.value === this.coinIdOptions.primary.value;
  }

  get #walletBalance(): string {
    return this.#isPrimaryCoinSelected ? this.wallet.balanceAvailable : this.wallet.networkBalance;
  }

  get #amountCtrl(): AbstractControl {
    return this.form.get('amount');
  }

  get #coinIdCtrl(): FormControl<string> {
    return this.form.get('coinId') as FormControl<string>;
  }

  get header() {
    return `${this.wallet.name}/${this.#translate.instant('transferDetails.title')}`;
  }

  constructor() {
    this.wallet = this.data.wallet;
    this.wallets = this.data.wallets.filter(item => this.wallet.id !== item.id && this.wallet.coin.id === item.coin.id);
  }

  ngOnInit(): void {
    this.#initCoinIdOptions();
    this.#initForm();
  }

  setTab(tab: TransferTabs): void {
    this.tab = tab;
    this.form.controls.toWalletId.reset();
    this.form.controls.address.reset();
    if (tab === 'internal') {
      this.form.controls.toWalletId.addValidators(Validators.required);
      this.form.controls.address.removeValidators(Validators.required);
    } else {
      this.form.controls.toWalletId.removeValidators(Validators.required);
      this.form.controls.address.addValidators(Validators.required);
    }
  }

  next(): void {
    if (this.form.invalid) {
      return;
    }

    const walletId = this.form.value.toWalletId;
    const walletTo = this.tab === 'internal' ? this.wallets.find(item => item.id === walletId) : null;

    walletTo
      ? this.#store.getEstimatesTransferInternal({
          body: { ...this.form.value, coinId: +this.#coinIdCtrl.value },
          orgId: this.wallet.organizationId,
        })
      : this.#store.getEstimatesTransferExternal({
          body: { ...this.form.value, coinId: +this.#coinIdCtrl.value },
          orgId: this.wallet.organizationId,
        });
  }

  close(): void {
    this.#dialogRef.close();
  }

  openFailedDialog(err: AppError): void {
    this.errMessage =
      err?.originalError?.error?.error_code === PAYOUT_ESTIMATE_ERROR
        ? 'transfer.form.payout.estimate.error'
        : (err?.originalError?.error?.error_description ?? 'Unknown error');

    this.#okDialog
      .open({
        headerKey: this.header,
        contentTpl: this.failedTpl(),
        noOk: true,
        noCancel: true,
      })
      .afterClosed()
      .subscribe(() => this.#dialogRef.close(false));
  }

  #openExecuteTransfer() {
    const walletId = this.form.value.toWalletId;
    const walletTo = this.tab === 'internal' ? this.wallets.find(item => item.id === walletId) : null;

    const { feeTotal, totalAmount, riskScorePassed, riskScore, riskLevel, riskScoreError, ourEstimatedFee } = this.#store.transaction();

    this.#transferDetailsDialog
      .open(
        {
          ...this.form.value,
          coinId: +this.#coinIdCtrl.value,
          feeTotal,
          totalAmount,
          riskScorePassed,
          riskScore,
          riskLevel,
          riskScoreError,
          ourEstimatedFee,
        },
        this.wallet,
        walletTo,
      )
      .subscribe(data => !isEmpty(data) && this.#dialogRef.close(data));
  }

  #initCoinIdOptions(): void {
    if (!this.wallet.secondaryWalletCoin) {
      return;
    }

    const coinSymbol = this.wallet.coin?.symbol;
    const secondaryCoinSymbol = this.wallet.secondaryWalletCoin?.symbol;
    this.coinIdOptions = {
      primary: {
        label: `${coinSymbol} (${formatBalanceByCurrency(this.wallet.balanceAvailable, coinSymbol)})`,
        value: this.wallet.coinId,
      },
      secondary: {
        label: `${secondaryCoinSymbol} (${formatBalanceByCurrency(this.wallet.networkBalance, secondaryCoinSymbol)})`,
        value: this.wallet.secondaryWalletCoinId.toString(),
      },
    };
  }

  #initForm(): void {
    this.form = this.#fb.group(
      {
        walletId: this.wallet.id,
        fromWalletId: this.wallet.id,
        toWalletId: [''],
        address: ['', Validators.required],
        amount: [''],
        coinId: new FormControl(this.wallet.coinId, { updateOn: 'change' }),
        totalAmount: '',
        feeTotal: '',
        comment: '',
      },
      { updateOn: 'blur' },
    );

    this.#setAmountValidators();
    this.#subscribeOnCoinChange();
  }

  #subscribeOnCoinChange(): void {
    this.#coinIdCtrl.valueChanges.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe(() => this.#setAmountValidators());
  }

  #setAmountValidators(): void {
    const maxDecimals = this.#isPrimaryCoinSelected ? this.wallet.coin.decimals : this.wallet.secondaryWalletCoin.decimals;

    const decimalLengthValidator = (control: AbstractControl) => {
      const value = control.value.replaceAll(',', '.');
      if (value && value.includes('.')) {
        const decimalPart = value.split('.')[1];
        if (decimalPart.length > maxDecimals) {
          return { maxDecimals: true };
        }
      }
      return null;
    };

    this.#amountCtrl.setValidators([
      Validators.required,
      validateTransferMinAmount(this.walletTransferMinAmount.toString()),
      Validators.pattern(digitPattern),
      validateTransferAmount(this.#walletBalance),
      decimalLengthValidator,
    ]);
    this.#amountCtrl.updateValueAndValidity();
  }
}
